import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import HubIcon from "@mui/icons-material/Hub";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  analyticsAPI,
  CoverageAccount,
  CoverageMonth,
  CoverageResponse,
  CoverageSource,
} from "../../services/api/analyticsApi";
import {
  plaidService,
  PlaidAccount,
} from "../../services/external/plaidService";

const COLUMN_WIDTH = 30;
const NAME_COLUMN_WIDTH = 240;
const MONTH_ABBR = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

const SOURCE_META: Record<
  CoverageSource,
  { label: string; icon: React.ElementType; color: string }
> = {
  plaid: {
    label: "Plaid / API connection",
    icon: CloudSyncIcon,
    color: "#1976d2",
  },
  api: {
    label: "Other API feed (e.g. Prime Agent Direct Connect)",
    icon: HubIcon,
    color: "#9c27b0",
  },
  agent_export: {
    label: "Agent browser export",
    icon: SmartToyIcon,
    color: "#00897b",
  },
  human_import: {
    label: "Human manual import",
    icon: UploadFileIcon,
    color: "#ef6c00",
  },
};

const SOURCE_ORDER: CoverageSource[] = [
  "plaid",
  "api",
  "agent_export",
  "human_import",
];

function parseYearMonth(ym: string): { year: number; month: number } {
  const [y, m] = ym.split("-").map((n) => parseInt(n, 10));
  return { year: y, month: m };
}

function addMonths(ym: string, delta: number): string {
  const { year, month } = parseYearMonth(ym);
  const total = year * 12 + (month - 1) + delta;
  const newYear = Math.floor(total / 12);
  const newMonth = (total % 12) + 1;
  return `${newYear}-${String(newMonth).padStart(2, "0")}`;
}

function monthsBetween(from: string, to: string): string[] {
  const result: string[] = [];
  let cursor = from;
  let guard = 0;
  while (cursor <= to && guard < 2000) {
    result.push(cursor);
    cursor = addMonths(cursor, 1);
    guard += 1;
  }
  return result;
}

function monthLabel(ym: string): string {
  const { month } = parseYearMonth(ym);
  return MONTH_ABBR[month - 1];
}

function maskLabel(mask: string | null): string {
  if (!mask) return "";
  return `••${mask}`;
}

function hasAnyData(month: CoverageMonth | undefined): boolean {
  if (!month) return false;
  return (
    month.plaid > 0 ||
    month.api > 0 ||
    month.agent_export > 0 ||
    month.human_import > 0
  );
}

function sourcesPresent(month: CoverageMonth | undefined): CoverageSource[] {
  if (!month) return [];
  return SOURCE_ORDER.filter((src) => month[src] > 0);
}

interface EnrichedAccount extends CoverageAccount {
  resolvedName: string;
  resolvedInstitution: string | null;
  resolvedMask: string | null;
}

const AccountCoverageTimeline: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CoverageResponse | null>(null);
  const [plaidAccounts, setPlaidAccounts] = useState<PlaidAccount[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [coverage, accounts] = await Promise.all([
          analyticsAPI.getCoverage(),
          plaidService.getAccountsCached(),
        ]);
        if (cancelled) return;
        setData(coverage);
        setPlaidAccounts(accounts);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load the account coverage timeline"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const plaidById = useMemo(() => {
    const map = new Map<string, PlaidAccount>();
    plaidAccounts.forEach((acc) => map.set(acc.account_id, acc));
    return map;
  }, [plaidAccounts]);

  const months = useMemo(() => {
    if (!data) return [];
    return monthsBetween(data.range.from, data.range.to);
  }, [data]);

  const years = useMemo(() => {
    const groups: { year: number; months: string[] }[] = [];
    months.forEach((ym) => {
      const { year } = parseYearMonth(ym);
      const last = groups[groups.length - 1];
      if (last && last.year === year) {
        last.months.push(ym);
      } else {
        groups.push({ year, months: [ym] });
      }
    });
    return groups;
  }, [months]);

  const enrichedAccounts: EnrichedAccount[] = useMemo(() => {
    if (!data) return [];
    return data.accounts.map((acc) => {
      const fallback = plaidById.get(acc.accountId);
      return {
        ...acc,
        resolvedName:
          acc.name || fallback?.name || fallback?.official_name || "Account",
        resolvedInstitution:
          acc.institutionName || fallback?.institution?.name || null,
        resolvedMask: acc.mask || fallback?.mask || null,
      };
    });
  }, [data, plaidById]);

  const monthSummary = useMemo(() => {
    const summary: Record<string, number> = {};
    months.forEach((ym) => {
      summary[ym] = enrichedAccounts.reduce(
        (count, acc) => count + (hasAnyData(acc.months[ym]) ? 1 : 0),
        0
      );
    });
    return summary;
  }, [months, enrichedAccounts]);

  const scrollByMonths = (count: number) => {
    scrollRef.current?.scrollBy({
      left: count * COLUMN_WIDTH,
      behavior: "smooth",
    });
  };

  const jumpToYear = (year: number) => {
    const index = months.findIndex((ym) => parseYearMonth(ym).year === year);
    if (index === -1 || !scrollRef.current) return;
    scrollRef.current.scrollTo({
      left: index * COLUMN_WIDTH,
      behavior: "smooth",
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowRight") {
      scrollByMonths(1);
      event.preventDefault();
    } else if (event.key === "ArrowLeft") {
      scrollByMonths(-1);
      event.preventDefault();
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || enrichedAccounts.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No account coverage data is available yet.
      </Alert>
    );
  }

  const gridTemplateColumns = `${NAME_COLUMN_WIDTH}px repeat(${months.length}, ${COLUMN_WIDTH}px)`;

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h6">Account Coverage Timeline</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {SOURCE_ORDER.map((src) => {
            const meta = SOURCE_META[src];
            const Icon = meta.icon;
            return (
              <Chip
                key={src}
                size="small"
                icon={<Icon sx={{ color: `${meta.color} !important` }} />}
                label={meta.label}
                variant="outlined"
              />
            );
          })}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Tooltip title="Back 12 months">
          <IconButton size="small" onClick={() => scrollByMonths(-12)}>
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
        {years.map((group) => (
          <Chip
            key={group.year}
            size="small"
            label={group.year}
            onClick={() => jumpToYear(group.year)}
            clickable
          />
        ))}
        <Tooltip title="Forward 12 months">
          <IconButton size="small" onClick={() => scrollByMonths(12)}>
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        ref={scrollRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          scrollBehavior: "smooth",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          outline: "none",
          "&:focus-visible": {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}
      >
        <Box
          sx={{ display: "grid", gridTemplateColumns, minWidth: "max-content" }}
        >
          {/* Year header row */}
          <Box
            sx={{
              position: "sticky",
              left: 0,
              top: 0,
              zIndex: 4,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
            }}
          />
          {years.map((group) => (
            <Box
              key={`year-${group.year}`}
              sx={{
                gridColumn: `span ${group.months.length}`,
                textAlign: "center",
                position: "sticky",
                top: 0,
                zIndex: 3,
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
                py: 0.25,
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                {group.year}
              </Typography>
            </Box>
          ))}

          {/* Month header row */}
          <Box
            sx={{
              position: "sticky",
              left: 0,
              top: 24,
              zIndex: 4,
              bgcolor: "background.paper",
              borderBottom: 2,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              px: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              Accounts with data
            </Typography>
          </Box>
          {months.map((ym) => (
            <Tooltip key={`mh-${ym}`} title={ym}>
              <Box
                sx={{
                  position: "sticky",
                  top: 24,
                  zIndex: 3,
                  bgcolor: "background.paper",
                  borderBottom: 2,
                  borderColor: "divider",
                  textAlign: "center",
                  fontSize: "0.65rem",
                  color: "text.secondary",
                  py: 0.25,
                }}
              >
                {monthLabel(ym)}
              </Box>
            </Tooltip>
          ))}

          {/* Summary row */}
          <Box
            sx={{
              position: "sticky",
              left: 0,
              zIndex: 2,
              bgcolor: "action.hover",
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              px: 1,
            }}
          >
            <Typography variant="caption" fontWeight={600}>
              Total ({enrichedAccounts.length} accounts)
            </Typography>
          </Box>
          {months.map((ym) => (
            <Box
              key={`sum-${ym}`}
              sx={{
                bgcolor: "action.hover",
                borderBottom: 1,
                borderColor: "divider",
                textAlign: "center",
                fontSize: "0.6rem",
                color: "text.secondary",
                py: 0.5,
              }}
            >
              {monthSummary[ym] > 0 ? monthSummary[ym] : ""}
            </Box>
          ))}

          {/* Account rows */}
          {enrichedAccounts.map((acc) => (
            <React.Fragment key={acc.accountId}>
              <Box
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  bgcolor: "background.paper",
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  px: 1,
                  py: 0.5,
                  overflow: "hidden",
                }}
              >
                <Typography variant="body2" noWrap fontWeight={500}>
                  {acc.resolvedName}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {acc.resolvedInstitution || "—"}{" "}
                    {maskLabel(acc.resolvedMask)}
                  </Typography>
                  {acc.type && (
                    <Chip
                      label={acc.type}
                      size="small"
                      variant="outlined"
                      sx={{ height: 16, fontSize: "0.55rem" }}
                    />
                  )}
                </Stack>
              </Box>
              {months.map((ym) => {
                const monthData = acc.months[ym];
                const present = hasAnyData(monthData);
                const sources = sourcesPresent(monthData);
                const tooltipText = present
                  ? `${ym} · ${acc.resolvedName}\n` +
                    sources
                      .map(
                        (src) => `${SOURCE_META[src].label}: ${monthData![src]}`
                      )
                      .join("\n")
                  : `${ym} · ${acc.resolvedName}: no data`;
                return (
                  <Tooltip
                    key={`${acc.accountId}-${ym}`}
                    title={
                      <span style={{ whiteSpace: "pre-line" }}>
                        {tooltipText}
                      </span>
                    }
                  >
                    <Box
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "1px",
                        opacity: present ? 1 : 0.3,
                      }}
                    >
                      {present &&
                        (sources.length <= 1 ? (
                          sources.map((src) => {
                            const Icon = SOURCE_META[src].icon;
                            return (
                              <Icon
                                key={src}
                                sx={{
                                  fontSize: 14,
                                  color: SOURCE_META[src].color,
                                }}
                              />
                            );
                          })
                        ) : (
                          <Stack
                            direction="row"
                            spacing={0}
                            alignItems="center"
                          >
                            {sources.slice(0, 4).map((src) => {
                              const Icon = SOURCE_META[src].icon;
                              return (
                                <Icon
                                  key={src}
                                  sx={{
                                    fontSize: 10,
                                    color: SOURCE_META[src].color,
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        ))}
                    </Box>
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default AccountCoverageTimeline;
